/**
 * Card UI for configuring a single outgoing Zapier webhook trigger.
 *
 * Used by:
 *   • Calendar settings → `appointmentBooked`
 *   • Tickets settings  → `ticketCreated`, `ticketStatusChanged`
 *
 * Pure presentation — host pages own the value + onChange. Includes a
 * "Test senden"-button that POSTs a sample payload to the configured URL
 * via the `testZapierWebhook` callable so users can verify their Zap is
 * reachable without waiting for a real ticket / booking.
 */
import * as React from 'react';
import { Loader2, SendHorizontal, Webhook, ExternalLink } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { useToast } from './ui/toast';
import type { ZapierWebhookEntry } from '@cp3/shared';

export type ZapierTrigger = 'ticketCreated' | 'ticketStatusChanged' | 'appointmentBooked';

interface Props {
  title: string;
  description: string;
  trigger: ZapierTrigger;
  value: ZapierWebhookEntry;
  onChange: (next: ZapierWebhookEntry) => void;
  /** Disabled while parent is saving. */
  saving?: boolean;
}

export function ZapierWebhookCard({
  title,
  description,
  trigger,
  value,
  onChange,
  saving,
}: Props) {
  const { toast } = useToast();
  const [testing, setTesting] = React.useState(false);
  const urlValid = value.url === '' || /^https:\/\/.+/i.test(value.url);

  async function onTest() {
    if (!value.url) {
      toast({
        title: 'Keine URL',
        description: 'Trag erst die Zapier-Webhook-URL ein.',
        variant: 'destructive',
      });
      return;
    }
    if (!urlValid) {
      toast({
        title: 'Ungültige URL',
        description: 'Die URL muss mit https:// beginnen.',
        variant: 'destructive',
      });
      return;
    }
    setTesting(true);
    try {
      const fn = httpsCallable<
        { url: string; trigger: ZapierTrigger },
        { ok: boolean; status?: number; attempts: number; error?: string }
      >(functions, 'testZapierWebhook');
      const r = await fn({ url: value.url, trigger });
      if (r.data.ok) {
        toast({
          title: 'Test erfolgreich',
          description: `Zapier hat den Payload akzeptiert (HTTP ${r.data.status}).`,
        });
      } else {
        toast({
          title: 'Test fehlgeschlagen',
          description:
            r.data.error ??
            (r.data.status ? `HTTP ${r.data.status}` : 'Unbekannter Fehler'),
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Test fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Webhook className="h-4 w-4" /> {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-medium">Aktiv</div>
            <p className="text-xs text-muted-foreground">
              Schalter aus = nichts wird an Zapier geschickt, auch wenn unten eine URL steht.
            </p>
          </div>
          <Switch
            checked={value.enabled}
            onCheckedChange={(v) => onChange({ ...value, enabled: v })}
            disabled={saving}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`zap-url-${trigger}`}>Zapier-Webhook-URL</Label>
          <Input
            id={`zap-url-${trigger}`}
            type="url"
            value={value.url}
            onChange={(e) => onChange({ ...value, url: e.target.value.trim() })}
            placeholder="https://hooks.zapier.com/hooks/catch/…"
            disabled={saving}
            spellCheck={false}
            autoComplete="off"
          />
          {!urlValid && (
            <p className="text-xs text-destructive">URL muss mit https:// beginnen.</p>
          )}
          <p className="text-xs text-muted-foreground">
            Erstelle einen Zap mit dem Trigger{' '}
            <span className="font-medium">"Webhooks by Zapier — Catch Hook"</span>,
            kopiere die Catch-URL und füge sie hier ein.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <a
            href="https://zapier.com/apps/webhook/integrations"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" /> Zapier Webhook-Doku
          </a>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onTest}
            disabled={testing || saving || !value.url || !urlValid}
          >
            {testing ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <SendHorizontal className="mr-2 h-3.5 w-3.5" />
            )}
            Test-Payload senden
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
